import { TranslateService } from '@ngx-translate/core';

import { AccessControl, Collaboration } from '@pncl/common-models';
import { AttendanceUserRole } from 'src/app/sessions/session/attendance/attendance-ui-models';
import { Feature, Personas } from '../services/acl.service';
import { PaywallConfig } from '../admin/types.interface';
import { IAppInformation } from '../sessions/session/iframe/additional-apps.utils';
import {
  BillingStatus,
  ChargebeeSubscriptionData,
  PaymentPlan,
  SUBSCRIPTION_TYPES,
} from './payment';
import { Context } from './context';
import { Fragment, Question } from './question';
import { RecordingAccess } from './space-recording';
import { Visibility } from './session';
import Capability = AccessControl.Capability;

// Mirrors EmailAudience in packages/models/src/feature/identity/models/institution.ts.
export enum EmailAudience {
  HOSTS_IN_SESSION = 'hosts-in-session',
  HOSTS_NOT_IN_SESSION = 'hosts-not-in-session',
  PARTICIPANTS_IN_SESSION = 'participants-in-session',
  PARTICIPANTS_NOT_IN_SESSION = 'participants-not-in-session',
}

export enum CONNECTION_TYPES {
  CHILD = 'child',
  SESSION_USER = 'session_user',
}

export enum INVITATION_STATE {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
}

export enum REVOKED_TYPES {
  SUSPENDED = 'SUSPENDED',
  DELETED = 'DELETED',
}

export enum CREATE_ACCOUNT_STATUS {
  SUSPENDED = 'SUSPENDED',
  DELETED = 'DELETED',
  IN_INSTITUTION = 'IN_INSTITUTION',
}

export enum EMAIL_CODE_ACTION {
  RESET_PASSWORD = 'resetPassword',
  VERIFY_EMAIL = 'verifyEmail',
  SIGN_IN = 'signIn',
}

export enum EMAIL_CODE_ERROR {
  LOCKOUT = 'LOCKOUT',
  INVALID_CODE = 'INVALID_CODE',
  EXPIRED = 'EXPIRED',
}

export interface Message {
  _id: string;
  // TODO(dt): Add mentions and likes.
  content: Fragment[];
  context: Context;

  // @TODO mfmansoo deprecate the timestamps https://pncl.atlassian.net/browse/BUGS-130
  published_timestamp: string;
  edited_timestamp: string;
  author: UserInfo;
  poster: UserInfo | BotInfo;
  posterType: PosterType;
  mentions: string[];
  log?: string;

  firstAttemptTimestamp?: string;
  parent?: string;
  createdAt: string;
  updatedAt: string;
  clicked?: boolean;

  deleted?: boolean;
  deletedByHost?: boolean;
  failed: boolean;
  isEdited?: boolean;

  currentSession?: string;
  currentBreakoutRoomUid?: string;
}

export interface MessageCreateResponse {
  message: string;
  message_id: string;
}

export interface MessageList {
  messages: Message[];
  total_messages: number;
  siblings?: UserInfo[];
  tutor_edits?: any[];
  user: User;
}

export interface LikeContext {
  question?: string;
  explanation?: string;
  worksheet?: string;
}

export interface LikeState {
  state: boolean;
  num_likes: number;
}

export interface Employment {
  startDate: string;
  endDate: string;
  employerName: string;
}

export interface Location {
  coordinates: number[];
  // The possiblities are:
  // 1) Point
  type: string;
}

export interface UserInfoBase {
  _id: string;
  courses: string[];
  dateOfBirth: string;
  experience: Employment[];
  location: Location;
  profile_image: string;
  name: string;
  org_name: string;
  email: string;
  role: string;
  questionAuthored: number;
  explanationAuthored: number;
  startDate: string;
  title: string;
}

export interface BotInfoBase {
  _id: string;
  name: string;
}

export interface UserInfo extends UserInfoBase {
  institution: string;
}

export interface BotInfo extends BotInfoBase {
  institution: string;
}

export enum PosterType {
  User = 'User',
  BotUser = 'BotUser',
}

/**
 * Virtual Background Settings for Institutions
 *
 * @property folder - Reference to the folder containing institution VB images
 * @property hasImages - True if institution has uploaded any VB images
 * @property isRestricted - True if users should only see institution VBs (no default/private)
 * Usage in space: Show strict mode if (isRestricted && hasImages)
 */
export interface VirtualBackgroundSettings {
  folder?: string;
  hasImages: boolean;
  isRestricted: boolean;
}

export interface InstitutionTheme {
  colors: { [key: string]: string };
  assets: {
    horizontalLogo?: string;
    waterMarkLogo?: string;
    squareLogo?: string;
    favicon?: string;
  };
  virtualBackgroundSettings?: VirtualBackgroundSettings;
}

export enum RecordingMode {
  AUTOMATIC = 'AUTOMATIC',
  MANUAL = 'MANUAL',
  DISABLED = 'DISABLED',
}

export enum InstitutionPermissionState {
  DEFAULT_ON = 'DEFAULT_ON',
  DEFAULT_OFF = 'DEFAULT_OFF',
  ALWAYS_ON = 'ALWAYS_ON',
  ALWAYS_OFF = 'ALWAYS_OFF',
}

export enum PostEventActionType {
  REDIRECT_URL = 'REDIRECT_URL',
}

export enum EventEndCountdownTimerVisibility {
  FIVE_MINUTES_BEFORE_EVENT_END = 'FIVE_MINUTES_BEFORE_EVENT_END',
  ALWAYS_SHOW = 'ALWAYS_SHOW',
}

export interface PostEventAction {
  enablePostEventAction: boolean;
  eventEndCountdownTimerVisibility?: EventEndCountdownTimerVisibility;
  enableExtendEvent: boolean;
  hostsPostEventActionType?: PostEventActionType;
  participantsPostEventActionType?: PostEventActionType;
  hostsPostEventActionMetadata?: Record<string, any>;
  participantsPostEventActionMetadata?: Record<string, any>;
}

export interface CrAdvancedSettings {
  egressType: Collaboration.Spaces.EgressType;
}

export enum HelpButtonState {
  DEFAULT = 'DEFAULT',
  CUSTOM = 'CUSTOM',
}

// Mirrors ShareSnippetsSettings, trigger and channel enum
// in packages/models/src/feature/identity/models/institution.ts.
export interface ShareSnippetsSettings {
  trigger: SnippetsNotificationAndVisibilityTrigger;
  channels: SnippetsNotificationChannel[];
}

// responsible for notification trigger and setting tab visibility for students/parents
// Automatically: Always show tab
// With approval: Requires approval to show tab
// Never: Never show tab
export enum SnippetsNotificationAndVisibilityTrigger {
  AUTOMATICALLY = 'AUTOMATICALLY',
  WITH_APPROVAL = 'WITH_APPROVAL',
  NEVER = 'NEVER',
}

export enum SnippetsNotificationChannel {
  EMAIL = 'EMAIL',
  SMS = 'SMS',
  WHATSAPP = 'WHATSAPP',
}

export const DEFAULT_SHARE_SNIPPETS_TRIGGER =
  SnippetsNotificationAndVisibilityTrigger.WITH_APPROVAL;

export interface InstitutionSettings {
  // ---- Admin Settings ----
  hidePoweredByPencil: boolean;
  postEventAction?: PostEventAction;
  crAdvancedSettings?: CrAdvancedSettings;
  productName?: string;
  homeRoute?: string;
  skipOnboarding?: boolean;
  // ---- User Settings ----
  addAdminsAsHostsInSpace: boolean;
  allowAdminsAsObserversInSpace: boolean;
  autoDesignateTeachersAsHosts: boolean;
  preventTeachersfromCreatingSpaces: boolean;
  preventStudentsFromEditingDetails: boolean;
  inactivityNotifications: boolean;
  crRecordingMode: RecordingMode;
  crEnableHostDeletion: boolean;
  crEnableDownloads: boolean;
  crEnablePublicRecordings: boolean;
  crForceAdminOnlyAccess: boolean;
  crDefaultAccess: RecordingAccess;
  crEnableAISummarization?: boolean;
  forceScreenShareInAutomaticCr: boolean;
  preventHostsFromRecordingLocally: boolean;
  crEnablePrivacyAwareCloudRecording: boolean;
  crRetentionPolicyDays?: number;
  liveCallAlerts: boolean;
  enableAutoStartCall: boolean;
  attendanceThresholds: AttendanceThresholdRule[];
  hideSpaceBackLinks: boolean; // reflects to the stand alone mode but the name was not chaged for backwards compatibility
  helpButtonBehaviour: {
    state: HelpButtonState;
    url: string;
  };
  enableNudityDetectionAlerts: boolean;
  preventEventEmails: boolean;
  aiCoachingInsights: boolean;
  preventMakingSpacesPublic: boolean;
  preventAddingExternalUsersToSpaces: boolean;
  defaultSpaceVisibility: Visibility;
  aiSafeguarding: boolean;
  aiSnippets: boolean;
  shareSnippetsSettings: ShareSnippetsSettings;
  summaryEmailAudience?: EmailAudience[];
  coachingEmailAudience?: EmailAudience[];
  snippetsEmailAudience?: EmailAudience[];
}

export enum AttendanceStatus {
  LATE = 'LATE',
  ABSENT = 'ABSENT',
}

export interface AttendanceThresholdRule {
  role: AttendanceUserRole;
  status: AttendanceStatus;
  minutes: number;
}

export interface InstitutionPermissions {
  enableWaitingRoom: InstitutionPermissionState;
  messageOtherParticipants: InstitutionPermissionState;
  leaderMode: InstitutionPermissionState;
  downloadSpacesOrBoards: InstitutionPermissionState;
  childMode: InstitutionPermissionState;
}

export interface Site {
  _id: string;
  institution: Institution;
  name: string;
  description?: string;
}

export interface LtiSetting {
  name?: string;
  client_id: string;
  deployment_id: string;
}

export interface EdlinkIntegration {
  syncedAt?: string;
  syncedUserCount?: number;
  syncedSiteCount?: number;
}

export interface Institution {
  _id?: string;
  description: string;
  email: string;
  name: string;
  phone: string;
  logo: string;
  location: string[];
  personas?: Record<string, IInstitutionPersona>;
  hero_image?: string;
  default_theme?: string;
  theme?: InstitutionTheme;
  settings?: InstitutionSettings;
  permissions?: InstitutionPermissions;
  brandChiron?: string;
  sites?: Site[];
  billingStatus?: BillingStatus;
  paywallConfig?: PaywallConfig;
  tierConfiguration?: TierConfiguration;
  ltiSettings?: LtiSetting[];
  apps?: IAppInformation[];

  // The properties below are not part of the persisted Institution DB model — the backend
  // adds them on top of the institution object in the API response (see tutor.ts institutionExtraFields).
  defaultSettings?: InstitutionSettings;
  // undefined = no integration configured; object present but syncedAt absent = configured, not yet synced; syncedAt present = full sync completed.
  edlinkIntegration?: EdlinkIntegration;
}

export interface TierConfiguration {
  tier: string;
}

export interface IInstitutionPersona {
  name: string;
  features: Feature[];
  accentColor: string;
}

export interface UserOverview {
  profiles: UserInfo[];
  institution: Institution;
}

export interface IndividualUserOverview extends UserInfoBase {
  questions: Question[];
  institution: Institution;
  description: string;
  directory_profile: any;
  subjects: any[];
  enabled_features: string[];
  isAnonymous?: boolean;
  personas?: string[];
}

export interface Connection {
  user: string;
  connectionType: CONNECTION_TYPES;
}

export interface Invitation {
  invitedBy: string;
  invitationState: INVITATION_STATE;
}

export interface IUserInfo {
  user: User;
  realtime: {
    realtime_user_notification: any;
    realtime_user_event_notification: any;
    realtime_rtm_channel: any;
  };
  intercom: { intercomToken: any };
  status_code: any;
}

export interface ISyllabus {
  user: User;
  total_questions: number;
  topics: TopicRootNode[];
  topics_coverage: CoverageStats[];
  topics_distribution: DistributionData[];
  tutor_stats: TutorStats[];
}
export interface DistributionData {
  data: {
    id: string;
    stats: DistributionStats;
  };
  children: DistributionData[];
}
export interface DistributionStats {
  numQuestions: number;
  numNotes: number;
  multipleChoiceQuestions: number;
  openEndedQuestions: number;
  badQuestions: number;
  averageQuestions: number;
  goodQuestions: number;
  excellentQuestions: number;
  veryEasyQuestions: number;
  easyQuestions: number;
  difficultQuestions: number;
  veryDifficultQuestions: number;
  topicIndex: number;
  distanceFromRoot: number;
}

export interface TutorStats {
  courseId: string;
  stats: Stats[];
}

export interface Stats {
  date: string;
  count: number;
}

export interface TimeZone {
  autoDetect: boolean;
  value: string;
}

/**
 * Persisted on a user when they complete a Chargebee institution checkout
 * but the frontend flow was interrupted before institution setup was finished.
 * On next app load, UserService detects this and re-opens the Institution Details Modal.
 */
export interface PendingUpgradeConfiguration {
  timestamp: string;
  paymentPlan: Partial<PaymentPlan>;
  subscriptionData: ChargebeeSubscriptionData;
}

export interface ParentDetails {
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  whatsappNumber?: string;
}

export interface User {
  courses: Course[];
  qualifications: any[];
  _id: string;
  externalId?: string;
  profile_image: string;
  name: string;
  org_name: string;
  email: string;
  role: string;
  institution: Institution;
  enabled_features?: string[];
  personas?: Personas[];
  settings?: Settings;
  billingStatus?: any;
  freeTrial?: boolean;
  isApiManaged?: boolean;
  isAnonymous?: boolean;
  /** True for a synthetic user representing an AI agent (the AI tutor, or a Practice
   * Roleplay persona) rather than a real person. Set server-side at mint time; used to
   * mark these rows as agents and hide human-only affordances (email, invite status). */
  isAgent?: boolean;
  feature_flags?: Record<string, boolean>;
  feature_flag_variables?: Record<string, Record<string, string | number>>;
  last_notifications_viewed_timestamp?: Date;
  phone?: string;
  parentDetails?: ParentDetails;
  phone_number_consent_accepted?: boolean;
  description?: string;
  questionsAuthored?: number;
  explanationsAuthored?: number;
  lastLogin: string | null;
  location: string;
  createdDate: string;
  sites?: Site[];
  password?: string;
  connections?: Connection[];
  invitation?: Invitation;
  info?: Info;
  legalAgreements?: LegalAgreements;
  createdAt: string;
  updatedAt: string;
  spacesFreeTimeLeft?: Date;
  revoked?: Revoked;
  custom_attributes?: string[];
  virtualBackgroundsFolder?: string;
  // this is the key to be used for public api call, this is refresh token for now
  generatedApiKey: boolean;
  subscriptionType?: SUBSCRIPTION_TYPES;
  apiKeyGeneratedOn: Date;
  defaultSpaceCreated: boolean;
  onboardingNextStepsHidden?: boolean;
  timezone: TimeZone;
  enabled_capabilities: Capability[];
  uniqueHash?: string;
  pendingUpgradeConfiguration?: PendingUpgradeConfiguration;
}

export enum OnlineStatus {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
  IN_CALL = 'IN_CALL',
  PENDING = 'PENDING',
}

export enum UserRole {
  HOST = 'host',
  PARTICIPANT = 'participant',
}

export type UserDataAndOnlineStatus = User & { presence?: OnlineStatus; role: UserRole };

export interface SpacePopulatedUser extends Omit<User, 'institution'> {
  institution: string;
}

export interface NewUser {
  name: string;
  email: string;
  personas: string[];
  password: string;
  sites?: string[];
  location?: string;
}

export interface Settings {
  lang_interface?: string;
  ui_theme?: string;
  inactivityNotifications?: string;
}

export interface Course {
  _id: string;
  grade: string;
  subject: string;
  syllabus_code: string;
  name: string;
  description?: string;
  image_url?: string;
  institution_name?: string;
  institution_logo?: string;
  institution?: Institution;
}

export interface CoverageStats {
  courseId: string;
  coverage: number;
  syllabus_code: string;
  question_count: number;
  note_count: number;
}

export interface Topic {
  topic: string;
  score: number;
}

export interface StudentStatistics {
  striveScore: number;
  totalPracticed: number;
  topicScores: Topic[];
}

export interface Student {
  fullName: string;
  profilePic: string;
  statistics: StudentStatistics;
}

export interface Syllabus {
  code: string;
  year: string;
  board: string;
  examination: string;
}

export interface TopicNode {
  _id: string;
  name: string;
  child: TopicNode;
  numbering: string;
}

export interface TopicRootNode {
  _id: string;
  syllabus: Syllabus;
  position: number;
  topic_node: TopicNode;
  name?: string;
}

export interface TopicList {
  topic_roots: TopicRootNode[];
}

export interface AttributeValue {
  _id: string;
  value: string;
}

export interface CustomAttribute {
  attribute_key: AttributeKey;
  attribute_value: AttributeValue[];
  type?: string;
  isMulti?: boolean;
}

export interface AttributeKey {
  _id: string;
  value: string;
}

export interface CustomAttributes {
  custom_attributes: CustomAttribute[];
}

export class UserDirectoryService {
  label = '';
  content = '';
  contact = '';
  link = '';
}

export class UserDirectoryEvent {
  month = '';
  date = '';
  name = '';
  location = '';
  description = '';
}

export class UserDirectoryExperience {
  institution = '';
  role = '';
  period = '';
  location = '';
  logo = '';
}

export const PERSONAS_TO_ROLES = {
  Administrator: 'Admin',
  CoachManager: 'Coach Manager',
  Teacher: 'Tutor',
  Student: 'Student',
  DataentryReviewer: 'Data Entry Reviewer',
  DataentryAgent: 'Data Entry Agent',
  InstitutionAdmin: 'Institution Admin',
  TeachingAssistant: 'Teaching Assistant',
};

export interface UserData {
  user: User;
  intercom?: any;
}

export enum USER_SCHOOL_ROLE {
  YES_PRIVATE = 'yes_private',
  YES_TA = 'yes_TA',
  NO_FUTURE = 'no_future',
  NO = 'no',
}

export enum USER_WORK_ROLE {
  TEACHER = 'teacher',
  TUTOR = 'tutor',
  ADMIN = 'admin',
  COACH = 'coach',
  OTHER = 'other',
}

export enum TeacherRoleDescription {
  INDEPENDENT_TUTOR = 'independent_tutor',
  TUTORING_COMPANY = 'tutoring_company',
  SCHOOL = 'school',
  TEACHING_SESSION = 'teaching_session',
  OTHER = 'other',
}

export enum AdminRoleDescription {
  INDEPENDENT_TUTOR = 'independent_tutor',
  SMALL_TUTORING_COMPANY = 'small_tutoring_company',
  LARGE_TUTORING_COMPANY = 'large_tutoring_company',
  SCHOOL = 'school',
  PARENT = 'parent',
  OTHER = 'other',
}

export interface Info {
  role?: string;
  userInSchool?: boolean;
  userSchoolRole?: USER_SCHOOL_ROLE;
  userWorkRole?: USER_WORK_ROLE;
  userWorkRoleOther?: string;
  roleType?: string;
  roleOther?: string;
  questionnaireComplete: boolean;
}

export interface LegalAgreements {
  acceptedAt?: string;
  termsVersion?: string;
  privacyVersion?: string;
}

export interface Revoked {
  type: REVOKED_TYPES;
  date: Date;
  note?: string;
  showRevokedModal: boolean;
  institution?: Institution; // set to optional for backwards compatibility
}

export enum SIGNUP_ROLES {
  SCHOOL_TEACHER = 'School Teacher',
  STUDENT = 'Student',
  OTHER = 'Other',
  TUTOR = 'Teacher / Tutor',
  TUTOR_ADMIN = 'School / Tutoring Administrator',
  TECH = 'Tech company',
  CREATOR = 'Construction, architecture & design',
  REAL_ESTATE = 'Real-estate',
  LEARNING = 'Learning & Development',
}

export const roleValueIDs = {
  student: 'student',
  teacher: 'teacher',
  admin: 'admin',
  other: 'other',
  techCompany: 'techCompany',
  construction: 'contruction',
  realEstate: 'realEstate',
  development: 'development',
};

export const roleIDs = {
  education: 'education',
  business: 'business',
};

export class UserConstantsTransformer {
  roleTypesDef: any;
  constructor(private translateService: TranslateService) {
    this.roleTypesDef = {
      [roleIDs.education]: this.translateService.instant('Education'),
      [roleIDs.business]: this.translateService.instant('Business'),
    };
  }

  get roleTypeIDs() {
    return roleIDs;
  }

  get roles() {
    return this.roleTypesDef;
  }

  get roleValueIds() {
    return roleValueIDs;
  }

  get roleTypes() {
    return {
      [roleIDs.education]: [
        {
          name: roleValueIDs.student,
          value: this.translateService.instant('Student'),
        },
        {
          name: roleValueIDs.teacher,
          value: this.translateService.instant('Teacher / Tutor'),
        },
        {
          name: roleValueIDs.admin,
          value: this.translateService.instant('School / Tutoring Administrator'),
        },
        {
          name: roleValueIDs.other,
          value: this.translateService.instant('Other (please specify)'),
        },
      ],
      [roleIDs.business]: [
        {
          name: roleValueIDs.techCompany,
          value: this.translateService.instant('Tech company'),
        },
        {
          name: roleValueIDs.construction,
          value: this.translateService.instant('Construction, architecture & design'),
        },
        {
          name: roleValueIDs.realEstate,
          value: this.translateService.instant('Real-estate'),
        },
        {
          name: roleValueIDs.development,
          value: this.translateService.instant('Learning & Development'),
        },
        {
          name: roleValueIDs.other,
          value: this.translateService.instant('Other (please specify)'),
        },
      ],
    };
  }
}

export type UserTypes = User | IndividualUserOverview | SpacePopulatedUser;
